import { useT } from "@/i18n";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AUTONOMY_ACTIONS, AUTONOMY_LEVELS, WORKFLOWS, LANGS } from "./constants";

const selectCls = "rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground";

export function LanguageField({ value, onChange }) {
  const t = useT();
  return (
    <div>
      <Label className="mb-1.5 block text-sm">{t("aipa.lang_label")}</Label>
      <select data-testid="aipa-lang" value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
        {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
      </select>
    </div>
  );
}

export function BriefingField({ settings, set }) {
  const t = useT();
  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-1.5 block text-sm">{t("aipa.briefing_time")}</Label>
        <Input type="time" data-testid="aipa-briefing-time" value={settings.briefing_time || "07:00"} onChange={(e) => set({ briefing_time: e.target.value })} className="w-32" />
      </div>
      <label className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
        <span className="text-sm text-foreground">{t("aipa.show_on_login")}</span>
        <Switch data-testid="aipa-show-login" checked={!!settings.show_on_login} onCheckedChange={(v) => set({ show_on_login: v })} />
      </label>
    </div>
  );
}

export function AutonomyField({ settings, set }) {
  const t = useT();
  const autonomy = settings.autonomy || {};
  return (
    <div className="space-y-2">
      {AUTONOMY_ACTIONS.map((a) => (
        <div key={a} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
          <span className="text-sm text-foreground">{t(`aipa.auto.${a}`)}</span>
          <select data-testid={`aipa-autonomy-${a}`} value={autonomy[a] || "confirm"} onChange={(e) => set({ autonomy: { ...autonomy, [a]: e.target.value } })} className={selectCls}>
            {AUTONOMY_LEVELS.map((lvl) => <option key={lvl} value={lvl}>{t(`aipa.lvl_${lvl}`)}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

export function WorkflowField({ settings, set }) {
  const t = useT();
  const wf = settings.workflows || {};
  return (
    <div className="space-y-2">
      {WORKFLOWS.map((w) => (
        <label key={w} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
          <span className="text-sm text-foreground">{t(`aipa.wf.${w}`)}</span>
          <Switch data-testid={`aipa-wf-${w}`} checked={!!wf[w]} onCheckedChange={(v) => set({ workflows: { ...wf, [w]: v } })} />
        </label>
      ))}
    </div>
  );
}

export function OutlookField({ settings, set }) {
  const t = useT();
  return (
    <div className="space-y-2">
      <label className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
        <span className="text-sm text-foreground">{t("aipa.outlook_calendar")}</span>
        <Switch data-testid="aipa-outlook-cal" checked={!!settings.outlook_calendar} onCheckedChange={(v) => set({ outlook_calendar: v })} />
      </label>
      <label className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
        <span className="text-sm text-foreground">{t("aipa.outlook_mail")}</span>
        <Switch data-testid="aipa-outlook-mail" checked={!!settings.outlook_mail} onCheckedChange={(v) => set({ outlook_mail: v })} />
      </label>
      <p className="text-xs text-muted-foreground">{t("aipa.outlook_not_connected")}</p>
    </div>
  );
}

export function GroupsField({ settings, set, groups }) {
  const t = useT();
  const sel = settings.group_filter || [];
  const toggle = (id) => set({ group_filter: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
  if (!groups.length) return <p className="text-sm text-muted-foreground">{t("aipa.groups_empty")}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {groups.map((g) => {
        const on = sel.includes(g.id);
        return (
          <button key={g.id} type="button" data-testid={`aipa-group-${g.id}`} onClick={() => toggle(g.id)}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {g.name || t("aipa.groups_all")}
          </button>
        );
      })}
    </div>
  );
}
